(ns app.front.view.group.reporting.inspections
  (:require [app.domain.group.model :as group-model]
            [app.domain.inspection.model :as inspection-model]
            [app.front.entities :as entities]
            [app.front.ui.filter-common :as filter-common]
            [app.front.util :refer [for-frag]]
            [app.front.view.group.admin.inspections.core :as gaic]
            [app.front.view.group.admin.subs :as gas]
            [app.front.view.group.reporting.common :as reporting-common]
            [app.front.view.group.reporting.download-button :as rdb]
            [app.front.view.group.reporting.filters :as reporting-filters]
            [app.front.view.group.reporting.form-fields :as reporting-ff]
            [app.front.view.group.reporting.list :as report-list]
            [app.query.filter-util :as filter-util]
            [app.reporting.model :as reporting-model]
            [clojure.set :as set]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [schema.core :as s]))

(def optional-fields-base
  [:reporting/municipality
   :reporting/case-description
   :reporting/case-description-of-location
   :reporting/case-in-proximity
   :reporting/case-non-owner-violation
   :reporting/inspection-created
   :reporting/inspection-priority])

(defn optional-fields []
  (into (if (ff/enabled? :feature-flag.id/property-db-units)
          [:reporting/application-unit-type-and-number]
          [])
        optional-fields-base))


(def default-settings
  (let [report-type (first reporting-model/inspection-report-types)]
    {:categories               #{}
     :sort-by                  #{(-> reporting-model/inspection-sort-options report-type first)}
     :report-type              #{report-type}
     :date-selector-mode       #{:reporting.date-range-options/current-month}
     :states                   #{}
     :inspections              #{}
     :selected-optional-fields #{}
     :selected-form-fields     nil}))


(rf/reg-event-db ::set-selected
  (fn [db [_ k v]]
    (assoc-in db [::data k] v)))


(rf/reg-sub ::data :-> ::data)


(rf/reg-sub ::report-type
  :<- [::data]
  (fn [data _]
    (::report-type data (first reporting-model/inspection-report-types))))


(rf/reg-sub ::available-sort-options
  :<- [::report-type]
  (fn [report-type _]
    (get reporting-model/inspection-sort-options report-type)))


(rf/reg-sub ::sort-option
  :<- [::data]
  :<- [::available-sort-options]
  (fn [[data sort-options] _]
    (or (some->> data
                 ::sort-option
                 ((set sort-options)))
        (first sort-options))))


(rf/reg-sub ::selected-states
  :<- [::data]
  #(-> % ::selected-states set))


(rf/reg-sub ::selected-inspections
  :<- [::data]
  #(-> % ::selected-inspections set))


(rf/reg-sub ::combined-inspection-types
  :<- [::entities/context]
  :<- [::gas/group]
  :<- [:language]
  :<- [::gas/superdomain-group?]
  (fn [[ctx group lang superdomain?] _]
    (let [groups   (if superdomain?
                     (->> group
                          group-model/descendant-authority-groups
                          set)
                     #{group})
          entities (->> (entities/find-entities ctx {:entity/type :entity.type/inspection-template-kind})
                        (filter #(contains? groups (:domain/authority-group %))))
          name-fn  #(inspection-model/inspection-template-kind-name % lang)]
      (reporting-common/combined-report-data entities name-fn))))


(rf/reg-event-fx ::select-saved-filter
  (fn [{:keys [db]} [_ selected-filter]]
    (let [filter-values      (filter-util/filter-entity->search-values-map selected-filter)
          type               (->> filter-values :report-type first)
          sort               (->> filter-values :sort-by first)
          date-selector-mode (->> filter-values :date-selector-mode first)]
      {:dispatch-n [[::reporting-common/set-selected-categories (:categories filter-values)]
                    [::set-selected ::selected-states (:states filter-values)]
                    [::set-selected ::selected-inspections (:inspections filter-values)]
                    [::reporting-common/set-category-missing-selected (first (:include-category-missing filter-values))]
                    [::reporting-ff/set-selected-from-filter filter-values]]
       :db         (-> db
                       (update ::data #(p/assoc-if %
                                                   ::report-type type
                                                   ::sort-option sort))
                       (p/assoc-if ::reporting-common/date-selector-mode date-selector-mode
                                   ::reporting-common/selected-optional-fields (:optional-fields filter-values)))})))


(rf/reg-event-db ::initialize-settings
  (fn [db _]
    (let [{:keys [categories sort-by report-type selected-optional-fields date-selector-mode inspections states]} default-settings]
      (-> db
          (update ::data #(assoc %
                                 ::report-type (first report-type)
                                 ::selected-states states
                                 ::selected-inspections inspections
                                 ::sort-option (first sort-by)))
          (assoc
            ::reporting-common/date-selector-mode (first date-selector-mode)
            ::reporting-common/selected-categories categories
            ::reporting-common/category-missing-selected? false
            ::reporting-common/selected-optional-fields selected-optional-fields
            ::reporting-ff/selected nil)))))


(rf/reg-sub ::filter-settings
  :<- [::gas/group]
  :<- [::reporting-common/selected-categories]
  :<- [::report-type]
  :<- [::sort-option]
  :<- [::reporting-common/date-selector-mode]
  :<- [::selected-states]
  :<- [::selected-inspections]
  :<- [::reporting-common/selected-optional-fields]
  :<- [::reporting-common/category-missing-selected?]
  :<- [::reporting-ff/sorted-selected-fields]
  (fn [[group
        categories
        report-type
        sort-by
        date-selector-mode
        states
        inspections
        optional-fields
        category-missing-selected?
        form-fields] _]
    (merge (util/mmap {:report-type        #{report-type}
                       :sort-by            #{sort-by}
                       :date-selector-mode #{date-selector-mode}
                       :group-id           #{(:db/id group)}}
                      categories
                      states
                      inspections
                      optional-fields
                      form-fields)
           (when category-missing-selected?
             {:include-category-missing #{category-missing-selected?}}))))


(rf/reg-sub ::filters-in-default-state?
  :<- [::filter-settings]
  (fn [filter-settings]
    (= (dissoc filter-settings :group-id) default-settings)))


(rf/reg-sub ::conditions
  :<- [::gas/group-id]
  :<- [::filter-settings]
  (fn [[group-id filter-settings]]
    (-> filter-settings
        (assoc :group-id #{group-id})
        filter-common/filter-values->conditions)))


(defn use-dates+state? [report-type]
  (contains? #{:reporting.inspections/inspections :reporting.inspections/visits} report-type))


(rf/reg-sub ::query
  :<- [::gas/group-id]
  :<- [::report-type]
  :<- [::reporting-common/selected-categories]
  :<- [::reporting-common/category-missing-selected?]
  :<- [::selected-inspections]
  :<- [::selected-states]
  :<- [::reporting-common/date-range]
  :<- [::sort-option]
  :<- [::reporting-common/selected-optional-fields]
  :<- [::reporting-ff/sorted-selected-fields]
  (fn [[group-id report-type categories category-missing-selected? template-ids
        states [start-date end-date] sort-option optional-fields form-fields] _]
    (-> {:group-id    group-id
         :report-type report-type}
        (p/assoc-if :categories (seq categories)
                    :include-category-missing category-missing-selected?
                    :template-ids (when (not= report-type :reporting.inspections/open-orders)
                                    (seq template-ids))
                    :states (when (use-dates+state? report-type) (seq states))
                    :start-date (when (use-dates+state? report-type) start-date)
                    :end-date (when (use-dates+state? report-type) end-date)
                    :sorting sort-option
                    :optional-fields optional-fields
                    :form-fields form-fields))))


(defn state-selector [tr]
  (let [selected-states (<sub [::selected-states])
        on-select-all   #(>evt [::set-selected ::selected-states (set inspection-model/inspection-states)])
        on-deselect-all #(>evt [::set-selected ::selected-states #{}])]
    [:div
     [reporting-common/selector-header tr :reporting.inspections/status on-select-all on-deselect-all]
     [:div.flex.flex-row.flex-wrap
      (for-frag [state inspection-model/inspection-states]
        [:div.w-56 {:key (name state)}
         (cc/checkbox {:id         (str (name state) "-checkbox")
                       :label-text (tr [state])
                       :checked    (contains? selected-states state)
                       :on-change  #(>evt [::set-selected
                                           ::selected-states
                                           ((if % conj disj) selected-states state)])})])]]))


(defn inspection-type-selector [tr]
  (let [inspection-types     (<sub [::combined-inspection-types])
        selected-inspections (<sub [::selected-inspections])
        on-select-all        #(>evt [::set-selected ::selected-inspections (apply set/union (vals inspection-types))])
        on-deselect-all      #(>evt [::set-selected ::selected-inspections #{}])]
    [:div
     [reporting-common/selector-header tr :reporting.inspections/type on-select-all on-deselect-all]
     [:div.columns-1.sm:columns-2.md:columns-3
      (for-frag [[name id-set] inspection-types]
        [:div.w-56
         (cc/checkbox {:id         (str id-set "-checkbox")
                       :label-text name
                       :checked    (set/subset? id-set selected-inspections)
                       :on-change  #(>evt [::set-selected
                                           ::selected-inspections
                                           ((if % set/union set/difference) selected-inspections id-set)])})])]]))


(defn sort-selector [tr]
  (when-let [sort-options (seq (<sub [::available-sort-options]))]
    (let [sort-option (<sub [::sort-option])]
      [:div.flex.items-center.mb-6 {:style {:width "550px"}}
       [:span.mr-4.whitespace-nowrap (tr [:reporting/sort-by])]
       (into
         (cc/content-switcher
           {:selected-index (p/find-index sort-options
                                          #(= % sort-option))
            :on-change      #(>evt [::set-selected ::sort-option (->> % .-index (nth sort-options))])
            :class-name     "-mt-2 w-full"})
         (for [k sort-options]
           (cc/switch {:text (tr [k])})))])))


(defn view [match]
  (let [tr                (<sub [::gas/group-tr])
        report-type       (<sub [::report-type])
        query             (<sub [::query])
        dates+state?      (use-dates+state? report-type)
        code-enforcement? (<sub [::gas/code-enforcement?])
        auth-group?       (<sub [::gas/auth-group?])]
    [reporting-common/report-page
     match
     tr
     [:section
      [reporting-filters/filters tr {:filter-type             :filter.type/reporting.inspections
                                     :filter-settings-sub     ::filter-settings
                                     :default-filters-sub     ::filters-in-default-state?
                                     :conditions-sub          ::conditions
                                     :select-saved-filter-evt ::select-saved-filter
                                     :initialize-settings-evt ::initialize-settings}]]
     [:section
      [:h3 (tr [:reporting/select-report])]
      [reporting-common/report-type-selector
       {:tr                  tr
        :label-loc           :reporting/inspection-type
        :on-change           #(>evt [::set-selected ::report-type (nth reporting-model/inspection-report-types %)])
        ;; TODO orders report is hidden for CE since orders can't yet be created. WIN-2504
        :report-type-options (cond-> reporting-model/inspection-report-types
                               code-enforcement? butlast)
        :selected-index      (p/find-index reporting-model/inspection-report-types
                                           #(= % report-type))}]
      (when dates+state?
        [:div.w-full
         [reporting-common/date-range-selector tr "inspections-date-range" reporting-common/date-range-options]])]
     [:section
      (when (or auth-group?
                (ff/enabled? :feature-flag.id/superdomain-can-manage-forms))
        [:div.w-full.mb-6
         [reporting-common/category-selector tr true]])
      (when dates+state?
        [:div.w-full.mb-6
         [state-selector tr]])
      (when (not= report-type :reporting.inspections/open-orders)
        [:div.w-full.mb-8
         [inspection-type-selector tr]])

      [:div.w-full.mb-8
       [reporting-common/optional-field-selector tr :inspections (optional-fields)]]

      (when (or auth-group?
                (ff/enabled? :feature-flag.id/superdomain-can-manage-forms))
        [:div.w-full.mb-8
         [reporting-ff/form-field-selector tr false]])]

     [sort-selector tr]

     [report-list/list-section (util/mmap tr report-type)]
     [rdb/generate-button (util/mmap tr query)]]))


(def route
  {:name        :view/group.reporting.inspections
   :view        view
   :parameters  {:path {:group-id s/Int}}
   :controllers [{:parameters {:path [:group-id]}
                  :start      (fn [params]
                                (let [group-id (-> params :path :group-id)]
                                  (>evt [::reporting-filters/fetch-saved-filters group-id])
                                  (>evt [::reporting-common/init group-id (first reporting-common/date-range-options)])
                                  (>evt [::gaic/fetch-inspections group-id])))
                  :stop       (fn []
                                (>evt [::initialize-settings]))}]})
